import { isAccessDeniedError } from '@/api';
import { flattenCursorPages } from '@/api/cursorPagination';
import { EmptyState } from '@/components';
import { PageContentCard } from '@/components/layout/PageContentCard';
import { useCompanyLevelName } from '@/features/companies/hooks';
import { useCurrentCompany } from '@/hooks/useCurrentCompany';
import { useInfiniteScrollSentinel } from '@/hooks/useInfiniteScrollSentinel';
import { useParams } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeviceAssignmentHistory } from '../hooks';
import type { DeviceAssignment } from '../types';
import { AssignmentHistoryCard } from './assignment/AssignmentHistoryCard';
import { AssignmentGapDivider } from './assignment/AssignmentGapDivider';
import { DeviceTabAccessDenied, DeviceTabError, DeviceTabLoading } from './DeviceTabStates';

// DeviceAssignmentTab lists every company the device was assigned to, newest first, as an
// endless-scrolling timeline. The first row is the current assignment. Company names and
// levels are today's values, not a snapshot from the assignment date.
export function DeviceAssignmentTab() {
  const { t } = useTranslation();
  const { deviceId } = useParams({ strict: false }) as { deviceId: string };
  const { companyId } = useCurrentCompany();
  const { resolveLevelName } = useCompanyLevelName();

  const historyQuery = useDeviceAssignmentHistory(deviceId, companyId);
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = historyQuery;
  const sentinelRef = useInfiniteScrollSentinel({ hasNextPage, isFetchingNextPage, fetchNextPage });
  const assignments = useMemo(() => flattenCursorPages(historyQuery.data?.pages), [historyQuery.data]);

  if (historyQuery.isLoading) {
    return <DeviceTabLoading />;
  }

  if (historyQuery.isError) {
    if (isAccessDeniedError(historyQuery.error)) {
      return (
        <DeviceTabAccessDenied
          title={t('devices.detail.assignment.accessDenied.title')}
          description={t('devices.detail.assignment.accessDenied.description')}
        />
      );
    }
    return <DeviceTabError title={t('devices.detail.assignment.error')} />;
  }

  const hasReachedEnd = !hasNextPage && assignments.length > 0;

  return (
    <PageContentCard className="w-full">
      {/* Card header */}
      <div className="mb-4">
        <h2 className="text-onsurface text-lg font-semibold">{t('devices.detail.assignment.title')}</h2>
        <p className="text-muted text-sm">{t('devices.detail.assignment.description')}</p>
      </div>

      <AssignmentTimeline assignments={assignments} resolveLevelName={resolveLevelName} />

      {isFetchingNextPage && <DeviceTabLoading rows={2} height={70} className="mt-3 pl-10" />}
      {/* Sentinel triggers the next page when scrolled into view */}
      <div ref={sentinelRef} aria-hidden="true" />
      {hasReachedEnd && (
        <p className="text-muted mt-6 ml-10 text-sm">{t('devices.detail.assignment.endOfHistory')}</p>
      )}
    </PageContentCard>
  );
}

interface AssignmentTimelineProps {
  assignments: DeviceAssignment[];
  resolveLevelName: (levelId: string | undefined) => string | undefined;
}

// AssignmentTimeline draws the vertical line with one card per assignment and a neutral
// divider wherever a hidden assignment sits between two rows.
function AssignmentTimeline({ assignments, resolveLevelName }: AssignmentTimelineProps) {
  const { t } = useTranslation();

  if (assignments.length === 0) {
    return <EmptyState title={t('devices.detail.assignment.empty')} />;
  }

  return (
    <div className="relative">
      {/* Vertical timeline line, centered on the dot column (TimelineDot is w-6 wide) */}
      <div className="bg-border absolute top-4 bottom-4 left-3 w-px -translate-x-1/2 mt-1" aria-hidden="true" />
      <ol className="flex flex-col gap-3">
        {assignments.map((assignment, index) => (
          <li key={`${assignment.company.id}-${assignment.assignedAt}`} className="flex flex-col gap-3">
            <AssignmentHistoryCard
              assignment={assignment}
              isCurrent={index === 0}
              levelName={resolveLevelName(assignment.company.levelId)}
            />
            {assignment.hasGapBeforeNext && <AssignmentGapDivider />}
          </li>
        ))}
      </ol>
    </div>
  );
}
